/* Bundle builder (sections/cs-bundle-builder.liquid)
   <cs-bundle-builder>: Stuff It adds a product to the stocking (nothing goes
   to the cart yet). The product's photo flies into its spot in the stocking
   (set on its block) and joins the panel's list; tapping Stuffed takes it
   back out. Add to Cart adds every stuffed product at once and empties the
   stocking.

   Phones and tablets: a sticky bar (thumbnails of what's stuffed, the count
   and Add to Cart) shows while the products are on screen and the stocking
   and panel aren't. While it shows, stuffed products fly to the bar instead
   (the stocking still fills in the background).
*/

if (!customElements.get('cs-bundle-builder')) {
    class CsBundleBuilder extends HTMLElement {
        connectedCallback() {
            this.items = Array.from(this.querySelectorAll('[data-item]'));
            this.stocking = this.querySelector('[data-stocking]');
            this.peek = this.querySelector('[data-peek]');
            this.list = this.querySelector('[data-list]');
            this.addButton = this.querySelector('[data-add]');
            this.addLabel = this.querySelector('[data-add-label]');
            this.addCompare = this.querySelector('[data-add-compare]');
            this.errorMessage = this.querySelector('[data-error]');
            // Stuffed items, in the order they went in
            this.stuffed = [];
            // Each stuffed item's image in the stocking, kept until it's taken out
            this.peekImages = new Map();

            this.bar = this.querySelector('[data-bar]');
            this.barThumbs = this.querySelector('[data-bar-thumbs]');
            this.barMore = this.querySelector('[data-bar-more]');
            this.barCount = this.querySelector('[data-bar-count]');
            this.barAdd = this.querySelector('[data-bar-add]');
            this.barAddLabel = this.querySelector('[data-bar-add-label]');
            this.productsInView = false;
            this.stockingInView = false;

            this.items.forEach((item) => {
                item.querySelector('[data-stuff]')?.addEventListener('click', () => this.toggle(item));
            });
            this.addButton?.addEventListener('click', () => this.addToCart());
            this.barAdd?.addEventListener('click', () => this.addToCart());
            this.querySelector('[data-bar-jump]')?.addEventListener('click', () => {
                this.stocking?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
            this.watchVisibility();

            // Theme editor: selecting a Product block stuffs it, to show where
            // it sits in the stocking
            this.onBlockSelect = (event) => {
                const item = this.items.find((entry) => entry.dataset.blockId === event.detail.blockId);
                if (item && !this.stuffed.includes(item)) this.toggle(item);
            };
            document.addEventListener('shopify:block:select', this.onBlockSelect);

            this.render();
        }

        disconnectedCallback() {
            document.removeEventListener('shopify:block:select', this.onBlockSelect);
            this.productsObserver?.disconnect();
            this.stockingObserver?.disconnect();
            document.documentElement.classList.remove('cs-bundle-bar-open');
        }

        toggle(item) {
            const index = this.stuffed.indexOf(item);
            if (index === -1) {
                this.stuffed.push(item);
                this.render(item);
                if (this.isBarShowing()) {
                    // The stocking is off screen: fill it now, fly to the bar
                    this.showLanding();
                    this.flyToBar(item);
                } else {
                    this.flyIntoStocking(item);
                }
            } else {
                this.stuffed.splice(index, 1);
                this.render();
            }
        }

        /* Panel, buttons and the peek ------------------------------------ */

        render(newItem = null) {
            this.items.forEach((item) => {
                const isStuffed = this.stuffed.includes(item);
                item.classList.toggle('is-stuffed', isStuffed);
                item.querySelector('[data-stuff]')?.setAttribute('aria-pressed', isStuffed ? 'true' : 'false');
            });

            if (this.list) {
                this.list.replaceChildren(
                    ...this.stuffed.map((item) => {
                        const row = document.createElement('li');
                        const name = document.createElement('span');
                        const price = document.createElement('span');
                        name.textContent = item.dataset.name;
                        price.textContent = ProCart.formatMoney(Number(item.dataset.price));
                        row.append(name, price);
                        return row;
                    })
                );
            }

            const total = this.stuffed.reduce((sum, item) => sum + Number(item.dataset.price), 0);
            const compareTotal = this.stuffed.reduce((sum, item) => sum + Number(item.dataset.comparePrice), 0);
            // Empty: the panel shows its empty title and no button on desktop,
            // and hides below desktop (CSS)
            this.classList.toggle('is-empty', this.stuffed.length === 0);
            if (this.addButton) {
                this.addLabel.textContent = `${this.addButton.dataset.label} ${ProCart.formatMoney(total)}`;
                this.addCompare.hidden = !(compareTotal > total);
                this.addCompare.textContent = compareTotal > total ? ProCart.formatMoney(compareTotal) : '';
            }

            this.renderPeek(newItem);
            this.renderBar(newItem, total);
            this.updateBar();
        }

        // Every stuffed product pokes out of the stocking at its block's spot
        // and tilt, so stuffing another never moves the ones already in.
        // Later ones are drawn in front. A newly stuffed one waits, hidden,
        // for the flying copy to land (flyIntoStocking).
        renderPeek(newItem) {
            if (!this.peek) return;

            this.peekImages.forEach((image, item) => {
                if (this.stuffed.includes(item)) return;
                image.remove();
                this.peekImages.delete(item);
            });

            this.stuffed.forEach((item) => {
                if (this.peekImages.has(item)) return;
                const source = item.querySelector('[data-item-image] img');
                const image = document.createElement('img');
                image.className = 'cs-bundle-builder__peek-item';
                image.alt = '';
                if (source) image.src = source.currentSrc || source.src;

                image.style.setProperty('--peek-x', `${Number(item.dataset.x) || 0}%`);
                image.style.setProperty('--peek-y', `${Number(item.dataset.y) || 0}%`);
                image.style.setProperty('--peek-rotate', `${Number(item.dataset.tilt) || 0}deg`);

                if (item === newItem) {
                    image.dataset.landing = '';
                    image.style.visibility = 'hidden';
                }
                this.peek.append(image);
                this.peekImages.set(item, image);
            });
        }

        /* Sticky bar ---------------------------------------------------- */

        // The bar shows once something is stuffed, while the products are on
        // screen and the stocking and panel aren't (and only where the CSS
        // shows it: phones and tablets)
        watchVisibility() {
            if (!this.bar || !('IntersectionObserver' in window)) return;
            const products = this.querySelector('.cs-bundle-builder__grid');
            const stockingParts = [this.stocking, this.querySelector('.cs-bundle-builder__panel')].filter(Boolean);
            const visibleParts = new Set();

            this.productsObserver = new IntersectionObserver((entries) => {
                this.productsInView = entries[0].isIntersecting;
                this.updateBar();
            });
            this.productsObserver.observe(products);

            // "In view" once half of the stocking (or panel) is on screen, so
            // a sliver of it peeking up from below still gets the bar
            this.stockingObserver = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) visibleParts.add(entry.target);
                        else visibleParts.delete(entry.target);
                    });
                    this.stockingInView = visibleParts.size > 0;
                    this.updateBar();
                },
                { threshold: 0.5 }
            );
            stockingParts.forEach((part) => this.stockingObserver.observe(part));
        }

        isBarShowing() {
            return Boolean(this.bar?.classList.contains('is-visible'));
        }

        updateBar() {
            if (!this.bar) return;
            const isAvailable = getComputedStyle(this.bar).display !== 'none';
            const show =
                isAvailable && this.stuffed.length > 0 && this.productsInView && !this.stockingInView;

            this.bar.classList.toggle('is-visible', show);
            this.bar.setAttribute('aria-hidden', show ? 'false' : 'true');
            this.bar.inert = !show;

            // Lift the floating chat buttons above the bar (CSS)
            const root = document.documentElement;
            root.classList.toggle('cs-bundle-bar-open', show);
            if (show) root.style.setProperty('--cs-bundle-bar-height', `${this.bar.offsetHeight}px`);
        }

        // The last three stuffed as thumbnails (newest last), "+2" for the
        // rest, the count and the total. A new thumbnail grows in, or waits
        // hidden while a copy flies to it (flyToBar).
        renderBar(newItem, total) {
            if (!this.bar) return;
            const showing = this.stuffed.slice(-3);
            const flyingToBar = newItem && this.isBarShowing();

            this.barThumbs.replaceChildren(
                ...showing.map((item) => {
                    const source = item.querySelector('[data-item-image] img');
                    const thumb = document.createElement('img');
                    thumb.className = 'cs-bundle-builder__bar-thumb';
                    thumb.alt = '';
                    if (source) thumb.src = source.currentSrc || source.src;
                    if (item === newItem) {
                        if (flyingToBar) {
                            thumb.dataset.landing = '';
                            thumb.style.visibility = 'hidden';
                        } else {
                            thumb.classList.add('is-new');
                        }
                    }
                    return thumb;
                })
            );

            const extra = this.stuffed.length - showing.length;
            this.barMore.hidden = extra <= 0;
            this.barMore.textContent = extra > 0 ? `+${extra}` : '';
            this.barCount.textContent = (this.barCount.dataset.label || '').replace('[count]', this.stuffed.length);
            this.barAddLabel.textContent = this.stuffed.length
                ? `${this.addButton?.dataset.label || ''} ${ProCart.formatMoney(total)}`
                : this.addButton?.dataset.label || '';
        }

        // A copy of the product photo arcs down to its thumbnail in the bar,
        // then the thumbnail shows. Skipped for reduced motion.
        flyToBar(item) {
            const thumb = this.barThumbs?.querySelector('[data-landing]');
            const source = item.querySelector('[data-item-image] img');
            const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

            const land = () => {
                if (!thumb) return;
                delete thumb.dataset.landing;
                thumb.style.removeProperty('visibility');
            };

            if (!thumb || !source || reduceMotion || !source.animate) {
                land();
                return;
            }

            // Where the thumbnail ends up once the bar has finished sliding in
            const barRect = this.bar.getBoundingClientRect();
            const slideOffset = barRect.top - (window.innerHeight - this.bar.offsetHeight);
            const thumbRect = thumb.getBoundingClientRect();
            const toX = thumbRect.left + thumbRect.width / 2;
            const toY = thumbRect.top - slideOffset + thumbRect.height / 2;

            const from = source.getBoundingClientRect();
            const flyer = source.cloneNode();
            flyer.className = 'cs-bundle-builder__flyer cs-bundle-builder__flyer--to-bar';
            flyer.removeAttribute('srcset');
            flyer.src = source.currentSrc || source.src;
            Object.assign(flyer.style, {
                left: `${from.left}px`,
                top: `${from.top}px`,
                width: `${from.width}px`,
                height: `${from.height}px`,
            });
            document.body.append(flyer);

            const dx = toX - (from.left + from.width / 2);
            const dy = toY - (from.top + from.height / 2);
            const scale = thumbRect.width / from.width;

            flyer
                .animate(
                    [
                        { transform: 'translate(0, 0) scale(1)' },
                        {
                            transform: `translate(${dx * 0.5}px, ${dy * 0.5 - 60}px) scale(${(1 + scale) / 2})`,
                            offset: 0.5,
                        },
                        { transform: `translate(${dx}px, ${dy}px) scale(${scale})` },
                    ],
                    { duration: 550, easing: 'cubic-bezier(0.33, 0, 0.2, 1)' }
                )
                .finished.catch(() => {})
                .finally(() => {
                    land();
                    flyer.remove();
                });
        }

        // Shows a newly stuffed product in the stocking without a flight
        showLanding() {
            const landing = this.peek?.querySelector('[data-landing]');
            if (!landing) return;
            delete landing.dataset.landing;
            landing.style.removeProperty('visibility');
        }

        /* Stuffing animation --------------------------------------------- */

        // A copy of the product photo arcs from its cell to the product's spot
        // in the stocking and lands exactly there (same size and tilt), then
        // the product swaps in, so nothing moves after it lands. The copy flies
        // inside the products' layer, so it's always behind the stocking photo.
        // Skipped for reduced motion.
        flyIntoStocking(item) {
            const landing = this.peek?.querySelector('[data-landing]');
            const source = item.querySelector('[data-item-image] img');
            const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

            const land = () => {
                if (!landing) return;
                delete landing.dataset.landing;
                landing.style.removeProperty('visibility');
            };

            if (!landing || !source || reduceMotion || !source.animate) {
                land();
                return;
            }

            // Where the product sits: its bottom middle is at its block's spot
            // (% of the stocking photo), tilted around that point. Everything
            // below is measured from the photo frame (the products' layer).
            const area = this.peek.getBoundingClientRect();
            const anchorX = (area.width * (Number(item.dataset.x) || 0)) / 100;
            const anchorY = (area.height * (Number(item.dataset.y) || 0)) / 100;
            const tilt = Number(item.dataset.tilt) || 0;
            const radians = (tilt * Math.PI) / 180;
            const size = landing.offsetWidth;
            const toX = anchorX + (size / 2) * Math.sin(radians);
            const toY = anchorY - (size / 2) * Math.cos(radians);

            const sourceRect = source.getBoundingClientRect();
            const from = {
                left: sourceRect.left - area.left,
                top: sourceRect.top - area.top,
                width: sourceRect.width,
                height: sourceRect.height,
            };
            const flyer = source.cloneNode();
            flyer.className = 'cs-bundle-builder__flyer';
            flyer.removeAttribute('srcset');
            flyer.src = source.currentSrc || source.src;
            Object.assign(flyer.style, {
                left: `${from.left}px`,
                top: `${from.top}px`,
                width: `${from.width}px`,
                height: `${from.height}px`,
                objectPosition: 'center bottom',
            });
            this.peek.append(flyer);

            const dx = toX - (from.left + from.width / 2);
            const dy = toY - (from.top + from.height / 2);
            const scale = size / from.width;
            // Rises first, then settles into place. The arc stays inside the
            // section, which clips anything above it.
            const grid = this.querySelector('.cs-bundle-builder__grid').getBoundingClientRect();
            const headroom = Math.max(0, sourceRect.top - grid.top - 8);
            const lift = Math.min(140, Math.abs(dx) * 0.2 + 50, headroom);

            flyer
                .animate(
                    [
                        { transform: 'translate(0, 0) scale(1) rotate(0deg)' },
                        {
                            transform: `translate(${dx * 0.5}px, ${dy * 0.5 - lift}px) scale(${(1 + scale) / 2}) rotate(${tilt / 2}deg)`,
                            offset: 0.5,
                        },
                        { transform: `translate(${dx}px, ${dy}px) scale(${scale}) rotate(${tilt}deg)` },
                    ],
                    { duration: 600, easing: 'cubic-bezier(0.33, 0, 0.2, 1)' }
                )
                .finished.catch(() => {})
                .finally(() => {
                    land();
                    flyer.remove();
                });
        }

        /* Cart ----------------------------------------------------------- */

        // Adds every stuffed product at once, opens the cart drawer and empties
        // the stocking
        async addToCart() {
            if (!this.stuffed.length) return;
            if (this.addButton.getAttribute('aria-disabled') === 'true') return;
            this.addButton.setAttribute('aria-disabled', 'true');
            this.addButton.classList.add('loading');
            this.barAdd?.setAttribute('aria-disabled', 'true');
            this.barAdd?.classList.add('loading');
            this.showError('');

            try {
                // Adds every stuffed product and opens the cart drawer (assets/pro-cart.js)
                await ProCart.add(
                    this.stuffed.map((item) => ({ id: Number(item.dataset.variantId), quantity: 1 })),
                    { source: 'cs-bundle-builder' }
                );

                this.stuffed = [];
                this.render();
            } catch (error) {
                console.error('Error adding the stocking to the cart:', error);
                this.showError(error.message || ProCart.ERROR_MESSAGE);
            } finally {
                this.addButton.classList.remove('loading');
                this.addButton.removeAttribute('aria-disabled');
                this.barAdd?.classList.remove('loading');
                this.barAdd?.removeAttribute('aria-disabled');
            }
        }

        showError(message) {
            if (!this.errorMessage) return;
            this.errorMessage.textContent = message;
            this.errorMessage.hidden = !message;
        }
    }

    customElements.define('cs-bundle-builder', CsBundleBuilder);
}
